import { RefreshCw, Loader2, CheckCircle2, XCircle, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useCheckPrices } from "@/hooks/useCheckPrices";

interface CheckPricesButtonProps {
  tripId: string;
  /** Called when a check finishes so the page can refresh fares/plan data. */
  onComplete?: () => void;
  /**
   * A jobId for a check the server already started (e.g. on trip creation).
   * When present the button mounts already in the "Checking fares…" state and
   * polls that job — no click needed.
   */
  autoJobId?: string | null;
}

/**
 * B2 — "never leave the user with silence". The button enters a continuous
 * "Checking fares…" state on click and an inline strip reports live progress,
 * then a clear result. Returns both the button and its feedback strip.
 */
export default function CheckPricesButton({ tripId, onComplete, autoJobId }: CheckPricesButtonProps) {
  const { phase, message, start } = useCheckPrices(tripId, onComplete, autoJobId);
  const checking = phase === "checking";

  return (
    <>
      <Button variant="outline" size="sm" onClick={start} disabled={checking}>
        {checking ? (
          <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <RefreshCw className="mr-1.5 h-4 w-4" aria-hidden="true" />
        )}
        {checking ? "Checking fares…" : "Check Prices"}
      </Button>

      {phase !== "idle" && message && (
        <div
          role="status"
          aria-live="polite"
          className={
            "flex w-full items-center gap-2 rounded-md border px-4 py-3 text-sm font-medium " +
            (phase === "error"
              ? "border-red-200 bg-red-50 text-red-700"
              : phase === "done"
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-sky-200 bg-sky-50 text-sky-700")
          }
        >
          {phase === "error" ? (
            <XCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : phase === "done" ? (
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          ) : phase === "checking" ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden="true" />
          ) : (
            <Search className="h-4 w-4 shrink-0" aria-hidden="true" />
          )}
          {message}
        </div>
      )}
    </>
  );
}
